import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addCustomerActivity, customerActivities, type ActivityType, type CustomerActivity, type WorkCustomer } from '../api/client';
import { israelInput, israelUtc, requireCustomerActivities, requireCustomerActivity, workDate, workError } from '../lib/customerWork';
import CustomerReminderBoard, { CustomerReminderForm } from './CustomerReminderBoard';
import { markRivhitDocumentsDirty } from '../lib/rivhitDocumentsSync';

export default function CustomerActivities({ customer, onSaved }: { customer: WorkCustomer; onSaved?: () => void }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [items, setItems] = useState<CustomerActivity[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [kind, setKind] = useState<ActivityType>('visit');
  const [occurred, setOccurred] = useState(israelInput());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(false);
  const [followup, setFollowup] = useState<CustomerActivity | null>(null);
  const [revision, setRevision] = useState(0);
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => {
    let active = true;
    customerActivities(customer.customer_id, { provider: customer.provider }).then(({ data }) => {
      if (active) {
        setItems(requireCustomerActivities(data?.items, customer, t('customerWork.invalidActivityResponse')));
        setCursor(data.next_cursor);
      }
    }).catch(e => { if (active) setError(workError(e, t('customerWork.loadError'))); });
    return () => { active = false; };
  }, [customer.customer_id, customer.provider, t]);
  return <div className="space-y-5 rounded-xl border bg-gray-50 p-4">
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{t('customerWork.activities')}</h2>
        <button className="rounded border px-3 py-2 text-sm" onClick={() => setForm(!form)}>{t('customerWork.addActivity')}</button>
      </div>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {form && <form className="space-y-2" onSubmit={async e => {
        e.preventDefault(); setBusy(true); setError('');
        try {
          const { data } = await addCustomerActivity(customer.customer_id, {
            activity_type: kind, occurred_at: israelUtc(occurred, t('customerWork.invalidTime')),
            notes, provider: customer.provider, request_id: requestId.current,
          });
          const saved = requireCustomerActivity(data, customer, t('customerWork.invalidActivityResponse'));
          setItems(old => [saved, ...old.filter(item => item.activity_id !== saved.activity_id)]
            .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)));
          setForm(false); setNotes(''); requestId.current = crypto.randomUUID();
          if (onSaved) onSaved();
          else markRivhitDocumentsDirty();
        } catch (e) { setError(workError(e, t('customerWork.saveError'))); }
        finally { setBusy(false); }
      }}>
        <label className="block" htmlFor={`${id}-type`}>{t('customerWork.type')}</label>
        <select id={`${id}-type`} className="w-full rounded border p-2" value={kind} onChange={e => setKind(e.target.value as ActivityType)}>
          {(['visit', 'machine_replacement', 'maintenance', 'phone_call', 'note'] as const).map(type =>
            <option key={type} value={type}>{t(`customerWork.types.${type}`)}</option>)}
        </select>
        <label className="block" htmlFor={`${id}-occurred`}>{t('customerWork.occurred')}</label>
        <input id={`${id}-occurred`} type="datetime-local" required className="w-full rounded border p-2" value={occurred} onChange={e => setOccurred(e.target.value)} />
        <label className="block" htmlFor={`${id}-notes`}>{t('customerWork.notes')}</label>
        <textarea id={`${id}-notes`} required maxLength={4000} className="w-full rounded border p-2" value={notes} onChange={e => setNotes(e.target.value)} />
        <button disabled={busy || !notes.trim()} className="rounded border px-3 py-2 disabled:opacity-50">{t('common.save')}</button>
      </form>}
      {!items.length && !error && <p className="text-gray-500">{t('customerWork.noActivities')}</p>}
      {items.map(item => <article key={item.activity_id} className="rounded border bg-white p-3">
        <p className="font-medium">{t(`customerWork.types.${item.activity_type}`)}</p>
        <p className="text-xs text-gray-600">{workDate(item.occurred_at, i18n.language)} · {item.author_name}</p>
        <p className="whitespace-pre-wrap text-sm">{item.notes}</p>
        <button className="mt-2 text-sm text-primary-700 underline" onClick={() => setFollowup(item)}>{t('customerWork.followup')}</button>
      </article>)}
      {cursor && <button disabled={busy} className="rounded border px-3 py-2" onClick={async () => {
        setBusy(true);
        try {
          const { data } = await customerActivities(customer.customer_id, { provider: customer.provider, cursor });
          const loaded = requireCustomerActivities(data?.items, customer, t('customerWork.invalidActivityResponse'));
          setItems(old => [...old, ...loaded.filter(item => !old.some(prior => prior.activity_id === item.activity_id))]);
          setCursor(data.next_cursor);
        } catch (e) { setError(workError(e, t('customerWork.loadError'))); }
        finally { setBusy(false); }
      }}>{t('customerWork.loadMore')}</button>}
      {followup && <div className="space-y-2">
        <p>{t('customerWork.activitySaved')}</p>
        <CustomerReminderForm customer={customer} activityId={followup.activity_id} onCancel={() => setFollowup(null)}
          onSaved={() => { setFollowup(null); setRevision(n => n + 1); }} />
      </div>}
    </section>
    <CustomerReminderBoard customer={customer} revision={revision} />
  </div>;
}
